import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { CategoryPlaceholder, localName } from './AttractionCard';

const TYPE_META = {
  wilderness: { label:'野外探索', icon:'🌿', bg:'#14532d', accent:'#4ade80' },
  culture:    { label:'文化體驗', icon:'🏛️', bg:'#1e3a5f', accent:'#93c5fd' },
};
const getTypeMeta = (t) => TYPE_META[t] || TYPE_META.culture;

const DeepCard = ({ a, i, cityKey, t, lang, favorites, onToggleFav, onSelect }) => {
  const meta = getTypeMeta(a.deepType);
  const displayName = localName(a, lang);
  const isFav = favorites?.includes(a.name);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [a.imageUrl]);
  const showImg = a.imageUrl && !imgFailed;

  return (
    <div
      style={{ width:220, flexShrink:0, borderRadius:16, overflow:'hidden', cursor:'pointer',
        background:'white', boxShadow:'0 2px 10px rgba(0,0,0,0.08)', border:'1px solid #f1f5f9',
        transition:'all 0.2s' }}
      onClick={() => onSelect && onSelect(a, cityKey)}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 10px 24px rgba(0,0,0,0.14)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 2px 10px rgba(0,0,0,0.08)';}}>
      {/* Full-bleed image */}
      <div style={{ position:'relative', height:260, overflow:'hidden', background:'#1e293b' }}>
        {showImg
          ? <img src={a.imageUrl} alt={displayName} loading="lazy"
              style={{ width:'100%', height:'100%', objectFit:'cover' }}
              onError={() => setImgFailed(true)}/>
          : <CategoryPlaceholder category={a.category || 'nature'} dark/>}
        {/* Bottom gradient so the district/hot-score row stays legible over any photo */}
        <div style={{ position:'absolute', left:0, right:0, bottom:0, height:'45%',
          background:'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 100%)' }}/>
        {/* Deep-type ribbon (top-left) */}
        <div style={{ position:'absolute', top:10, left:10, background:'rgba(15,23,42,0.85)', backdropFilter:'blur(4px)',
          borderRadius:8, padding:'4px 10px', display:'flex', alignItems:'center', gap:5 }}>
          <span style={{ fontSize:11 }}>{meta.icon}</span>
          <span style={{ fontSize:10, fontWeight:900, color:meta.accent, letterSpacing:0.5 }}>{meta.label}</span>
        </div>
        {/* Fav button (top-right) */}
        <button aria-label={isFav?'移除收藏':'收藏'} onClick={e=>{e.stopPropagation();onToggleFav&&onToggleFav(a.name,cityKey);}}
          style={{ position:'absolute', top:8, right:8, width:32, height:32, borderRadius:'50%',
            background:'rgba(255,255,255,0.9)', border:'none', display:'flex', alignItems:'center',
            justifyContent:'center', cursor:'pointer', zIndex:2, boxShadow:'0 2px 6px rgba(0,0,0,0.15)' }}>
          <Heart size={15} color={isFav?'#ef4444':'#475569'} fill={isFav?'#ef4444':'none'}/>
        </button>
        {/* District tag + hot score (bottom-left, over the gradient) */}
        <div style={{ position:'absolute', left:12, bottom:10, display:'flex', alignItems:'center', gap:6 }}>
          {a.district && (
            <span style={{ background:'rgba(255,255,255,0.92)', color:'#1e293b', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:800 }}>{a.district}</span>
          )}
        </div>
      </div>
      {/* Title + rating (below image, white background) */}
      <div style={{ padding:'12px 14px 14px' }}>
        <p style={{ margin:'0 0 6px', fontSize:14, fontWeight:900, color:'#0f172a', lineHeight:1.3,
          display:'-webkit-box', WebkitLineClamp:1, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{displayName}</p>
        {a.rating && (
          <p style={{ margin:0, fontSize:12, color:'#64748b', fontWeight:700 }}>
            <span style={{ color:'#f59e0b' }}>★</span> {Number(a.rating).toFixed(1)}/5
          </p>
        )}
        {/* Route/itinerary details live in the attraction detail sheet
            (see fetchAttractionDetail/detail.route in EC_Travel_v2.jsx), not
            on this outer card — tap through to see them. */}
        {a.deepType === 'wilderness' && a.route && (
          <p style={{ margin:'6px 0 0', fontSize:10, color:'#16a34a', fontWeight:700 }}>🚶 {lang==='en'?'Tap for route':lang==='ja'?'タップでルート':lang==='ko'?'경로 보기':'點擊查看路線'}</p>
        )}
      </div>
    </div>
  );
};

const DEEP_PAGE_SIZE = 4;

const DEEP_TYPES = ['wilderness', 'culture'];

export const DeepStrip = ({ attractions, cityKey, t, favorites, onToggleFav, onSelect, districtKey }) => {
  const firstAvailable = (list) => DEEP_TYPES.find(k => list.some(a => a.deepType === k)) || DEEP_TYPES[0];
  const [filter, setFilter] = React.useState(() => firstAvailable(attractions || []));
  const [page, setPage] = React.useState(0);

  // Re-pick a default type (never "全部") whenever the district scope changes,
  // since the previously-selected type may no longer have any picks in it.
  React.useEffect(() => {
    setFilter(firstAvailable(attractions || []));
    setPage(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on district change, not every attractions re-render
  }, [districtKey, cityKey]);

  if (!attractions?.length) return null;

  const filtered = attractions.filter(a => a.deepType === filter);
  const totalPages = Math.ceil(filtered.length / DEEP_PAGE_SIZE);
  const pageItems  = filtered.slice(page * DEEP_PAGE_SIZE, (page + 1) * DEEP_PAGE_SIZE);
  const lang = t.aiLang === 'Traditional Chinese' ? 'zh-HK'
    : t.aiLang === 'Simplified Chinese' ? 'zh-CN'
    : t.aiLang === 'Japanese' ? 'ja'
    : t.aiLang === 'Korean' ? 'ko' : 'en';

  const labels = {
    title:    { 'zh-HK':'🗺️ 深度體驗', 'zh-CN':'🗺️ 深度体验', 'en':'🗺️ Deep Experiences', 'ja':'🗺️ 深い体験', 'ko':'🗺️ 심층 체험' },
    subtitle: { 'zh-HK':'野外 · 文化探索', 'zh-CN':'野外 · 文化探索', 'en':'Wilderness · Authentic culture', 'ja':'野外 · 文化体験', 'ko':'야외 · 문화 탐험' },
  };

  const filterBtns = [
    { k:'wilderness', label: TYPE_META.wilderness.label,    icon:'🌿' },
    { k:'culture',    label: TYPE_META.culture.label,       icon:'🏛️' },
  ];
  const emptyLabel = lang==='en' ? 'No picks in this district yet'
    : lang==='ja' ? 'この地区にはまだありません'
    : lang==='ko' ? '이 지역에는 아직 없습니다'
    : '這個地區暫無此類型的深度體驗';

  return (
    <div style={{ marginBottom:28 }}>
      <div style={{ marginBottom:12 }}>
        <p style={{ fontSize:17, fontWeight:900, color:'#0f172a', margin:'0 0 3px' }}>{labels.title[lang]||'🗺️ Deep Experiences'}</p>
        <p style={{ fontSize:11, color:'#64748b', margin:'0 0 10px' }}>{labels.subtitle[lang]}</p>
        {/* Filter buttons + pagination */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
            {filterBtns.map(btn => {
              const active = filter === btn.k;
              const meta   = btn.k !== 'all' ? TYPE_META[btn.k] : null;
              return (
                <button key={btn.k} onClick={() => { setFilter(btn.k); setPage(0); }}
                  style={{ padding:'5px 13px', borderRadius:20, border:'none', cursor:'pointer',
                    fontSize:11, fontWeight:900, transition:'all 0.15s',
                    background: active ? (meta ? meta.bg : '#0f172a') : '#f1f5f9',
                    color:      active ? (meta ? meta.accent : '#f1f5f9') : '#64748b',
                    boxShadow:  active ? `0 2px 8px ${meta ? meta.accent+'40' : '#00000030'}` : 'none' }}>
                  {btn.icon && <span style={{ marginRight:4 }}>{btn.icon}</span>}{btn.label}
                </button>
              );
            })}
          </div>
          {totalPages > 1 && (
            <span style={{ fontSize:11, color:'#64748b', fontWeight:700 }}>{page+1} / {totalPages}</span>
          )}
        </div>
      </div>
      {/* Cards with side arrows */}
      {filtered.length === 0 ? (
        <p style={{ fontSize:12, color:'#94a3b8', margin:'8px 0 0', textAlign:'center', padding:'20px 0' }}>{emptyLabel}</p>
      ) : (
      <div style={{ position:'relative' }}>
        {totalPages > 1 && page > 0 && (
          <button aria-label="上一頁" onClick={()=>setPage(p=>Math.max(0,p-1))}
            style={{ position:'absolute', left:-20, top:'50%', transform:'translateY(-50%)', zIndex:10,
              width:44, height:44, borderRadius:'50%', border:'1.5px solid #334155',
              background:'#1e293b', color:'#a5b4fc', cursor:'pointer', padding:0, lineHeight:1,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:24, fontWeight:900, boxShadow:'0 4px 16px rgba(99,102,241,0.25)' }}>‹</button>
        )}
        <div style={{ display:'flex', gap:14, overflowX:'auto', paddingBottom:8,
          scrollbarWidth:'none', msOverflowStyle:'none',
          marginLeft:-20, marginRight:-20, paddingLeft:20, paddingRight:20 }}>
          {pageItems.map((a, i) => (
            <DeepCard key={`${filter}-${page}-${i}`} a={a} i={page*DEEP_PAGE_SIZE+i} cityKey={cityKey} t={t} lang={lang}
              favorites={favorites} onToggleFav={onToggleFav} onSelect={onSelect}/>
          ))}
        </div>
        {totalPages > 1 && page < totalPages-1 && (
          <button aria-label="下一頁" onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))}
            style={{ position:'absolute', right:-20, top:'50%', transform:'translateY(-50%)', zIndex:10,
              width:44, height:44, borderRadius:'50%', border:'1.5px solid #334155',
              background:'#1e293b', color:'#a5b4fc', cursor:'pointer', padding:0, lineHeight:1,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:24, fontWeight:900, boxShadow:'0 4px 16px rgba(99,102,241,0.25)' }}>›</button>
        )}
      </div>
      )}
      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:10 }}>
          {Array.from({length:totalPages},(_,i)=>(
            <button key={i} aria-label={`第 ${i+1} 頁`} onClick={()=>setPage(i)}
              style={{ width:i===page?20:7, height:7, borderRadius:4, border:'none', background:i===page?'#6366f1':'#334155', cursor:'pointer', padding:0, transition:'all 0.25s' }}/>
          ))}
        </div>
      )}
    </div>
  );
};
